if (!customElements.get('shipping-protection')) {
  class ShippingProtection extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.input = this.querySelector('[data-shipping-protection-toggle]');
      this.error = this.querySelector('[data-shipping-protection-error]');
      this.feedback = this.querySelector('[data-shipping-protection-feedback]');
      this.cartDrawer = document.querySelector('cart-drawer');

      if (this.dataset.cleanupOnly === 'true') {
        window.setTimeout(() => this.removeOrphanedProtection(), 0);
        return;
      }

      if (!this.input) return;
      this.input.addEventListener('change', this.handleChange);

      if (Number(this.dataset.lineQuantity || 0) > 1) {
        window.setTimeout(() => this.normalizeQuantity(), 0);
      }
    }

    disconnectedCallback() {
      this.input?.removeEventListener('change', this.handleChange);
    }

    handleChange = () => {
      this.updateProtection(this.input.checked);
    };

    getSections() {
      if (!this.cartDrawer?.getSectionsToRender) return [];
      return [...new Set(this.cartDrawer.getSectionsToRender().map(section => section.section))];
    }

    setLoading(loading) {
      this.classList.toggle('is-loading', loading);
      this.setAttribute('aria-busy', loading ? 'true' : 'false');

      if (this.input) this.input.disabled = loading;

      const scope = this.closest('cart-drawer') || document;
      scope.querySelectorAll('[name="checkout"]').forEach(button => {
        button.disabled = loading;
        button.setAttribute('aria-disabled', loading ? 'true' : 'false');
      });
    }

    async requestCartChange(body, endpoint) {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      });
      const cartData = await response.json();

      if (!response.ok || cartData.status || cartData.errors) {
        throw new Error(cartData.description || cartData.message || 'The cart could not be updated.');
      }

      return cartData;
    }

    async updateProtection(shouldAdd, options = {}) {
      if (this.updating) return;
      this.updating = true;
      const previousState = !shouldAdd;
      const sections = this.getSections();
      const sharedBody = {
        sections,
        sections_url: window.location.pathname
      };

      this.setLoading(true);
      if (this.error) this.error.textContent = '';
      if (this.feedback) this.feedback.textContent = shouldAdd ? 'Adding…' : 'Removing…';

      try {
        let cartData;

        if (shouldAdd) {
          cartData = await this.requestCartChange(
            {
              ...sharedBody,
              items: [{ id: Number(this.dataset.variantId), quantity: 1 }]
            },
            routes.cart_add_url
          );
        } else {
          cartData = await this.requestCartChange(
            {
              ...sharedBody,
              id: this.dataset.lineKey || Number(this.dataset.variantId),
              quantity: 0
            },
            routes.cart_change_url
          );
        }

        ShippingProtection.pendingFeedback = options.silent
          ? ''
          : shouldAdd
            ? 'Shipping protection added.'
            : 'Shipping protection removed.';
        ShippingProtection.pendingLocation = this.dataset.location;

        if (this.cartDrawer?.renderContents && cartData.sections) {
          this.cartDrawer.renderContents(cartData, false);
        } else {
          window.location.reload();
          return;
        }

        if (typeof updateCartCounters === 'function') updateCartCounters();
        if (typeof updateFreeShipping === 'function') updateFreeShipping();

        if (!options.silent) {
          window.requestAnimationFrame(() => ShippingProtection.restoreFeedbackAndFocus());
        }
      } catch (error) {
        if (this.input) this.input.checked = previousState;
        if (this.feedback) this.feedback.textContent = '';
        if (this.error) this.error.textContent = `${error.message} Please try again.`;
        this.setLoading(false);
      } finally {
        this.updating = false;
      }
    }

    async normalizeQuantity() {
      if (this.updating || !this.dataset.lineKey) return;
      this.updating = true;

      try {
        const sections = this.getSections();
        const cartData = await this.requestCartChange(
          {
            id: this.dataset.lineKey,
            quantity: 1,
            sections,
            sections_url: window.location.pathname
          },
          routes.cart_change_url
        );

        if (this.cartDrawer?.renderContents && cartData.sections) {
          this.cartDrawer.renderContents(cartData, false);
        }
        if (typeof updateCartCounters === 'function') updateCartCounters();
        if (typeof updateFreeShipping === 'function') updateFreeShipping();
      } catch (error) {
        console.error('Shipping Protection quantity could not be normalized.', error);
      } finally {
        this.updating = false;
      }
    }

    async removeOrphanedProtection() {
      if (!this.dataset.lineKey || this.updating) return;
      this.updating = true;

      try {
        const sections = this.getSections();
        const cartData = await this.requestCartChange(
          {
            id: this.dataset.lineKey,
            quantity: 0,
            sections,
            sections_url: window.location.pathname
          },
          routes.cart_change_url
        );

        if (this.cartDrawer?.renderContents && cartData.sections) {
          this.cartDrawer.renderContents(cartData, false);
        }
        if (typeof updateCartCounters === 'function') updateCartCounters();
        if (typeof updateFreeShipping === 'function') updateFreeShipping();
      } catch (error) {
        console.error('Orphaned Shipping Protection could not be removed.', error);
      } finally {
        this.updating = false;
      }
    }

    static restoreFeedbackAndFocus() {
      const selector = ShippingProtection.pendingLocation
        ? `shipping-protection[data-location="${CSS.escape(ShippingProtection.pendingLocation)}"]`
        : 'shipping-protection:not([data-cleanup-only])';
      const nextControl = document.querySelector(selector);

      if (!nextControl) return;
      const nextInput = nextControl.querySelector('[data-shipping-protection-toggle]');
      const nextFeedback = nextControl.querySelector('[data-shipping-protection-feedback]');

      if (nextFeedback && ShippingProtection.pendingFeedback) {
        nextFeedback.textContent = ShippingProtection.pendingFeedback;
        window.setTimeout(() => {
          if (nextFeedback.isConnected) nextFeedback.textContent = '';
        }, 4000);
      }
      nextInput?.focus({ preventScroll: true });
      ShippingProtection.pendingFeedback = '';
      ShippingProtection.pendingLocation = '';
    }
  }

  ShippingProtection.pendingFeedback = '';
  ShippingProtection.pendingLocation = '';
  customElements.define('shipping-protection', ShippingProtection);
}
